import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

const BAND_COLORS = { red: "#ef4444", orange: "#f97316", yellow: "#eab308", green: "#22c55e", purple: "#8b5cf6" };
export function bandColor(score) {
    if (score <= 20) return BAND_COLORS.red;
    if (score <= 40) return BAND_COLORS.orange;
    if (score <= 60) return BAND_COLORS.yellow;
    if (score <= 80) return BAND_COLORS.green;
    return BAND_COLORS.purple;
}
export function bandLabel(score) {
    if (score <= 20) return "Critical";
    if (score <= 40) return "Weak";
    if (score <= 60) return "Average";
    if (score <= 80) return "Healthy";
    return "Excellent";
}

function useCountUp(target, ms = 900) {
    const [v, setV] = useState(0);
    useEffect(() => {
        let raf; const t0 = performance.now(); const from = 0;
        const tick = (t) => {
            const p = Math.min(1, (t - t0) / ms);
            setV(from + (target - from) * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, ms]);
    return v;
}

export function MoneyMeterGauge({ score = 0, future = null, size = 200, label, showFuture = true }) {
    const shown = useCountUp(score);
    const color = bandColor(score);
    const R = size / 2 - 16, C = 2 * Math.PI * R;
    const off = C - (shown / 100) * C;
    const hasFuture = showFuture && future != null && Math.round(future) !== Math.round(score);
    const diff = hasFuture ? Math.round((future - score) * 10) / 10 : 0;
    return (
        <div className="relative grid place-items-center" data-testid="money-meter-gauge" style={{ width: size, height: size }}>
            <div className="absolute rounded-full anim-pulse-glow" style={{ width: size * 0.8, height: size * 0.8, background: `radial-gradient(closest-side, ${color}33, transparent 70%)` }} />
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={R} stroke="rgba(0,0,0,0.06)" strokeWidth="12" fill="none" />
                {hasFuture && (
                    <circle cx={size / 2} cy={size / 2} r={R} stroke={bandColor(future)} strokeWidth="12" fill="none" strokeLinecap="round"
                        strokeDasharray={C} strokeDashoffset={C - (future / 100) * C} opacity="0.28" />
                )}
                <circle cx={size / 2} cy={size / 2} r={R} stroke={color} strokeWidth="12" fill="none" strokeLinecap="round"
                    strokeDasharray={C} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1)" }} />
            </svg>
            <div className="absolute text-center">
                <div className="font-display font-light leading-none" style={{ fontSize: size * 0.28, color }}>{Math.round(shown)}</div>
                <div className="text-[10px] font-semibold tracking-[0.18em] uppercase mt-1" style={{ color }}>{label || bandLabel(score)}</div>
                {hasFuture && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium" style={{ color: diff >= 0 ? "#16a34a" : "#e11d48" }} data-testid="money-meter-future">
                        <span className="text-neutral-400">→</span> {Math.round(future)} <span>({diff >= 0 ? "+" : ""}{diff})</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function Sparkline({ points = [], color = "#8b5cf6" }) {
    if (points.length < 2) return null;
    const w = 220, h = 44, vals = points.map((p) => p.score);
    const min = Math.min(...vals) - 2, max = Math.max(...vals) + 2;
    const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - ((p.score - min) / (max - min || 1)) * h}`).join(" ");
    return (
        <svg width={w} height={h} className="w-full" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
            <polyline points={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const PRESETS = [
    { label: "Hire 20 engineers", deltas: { monthly_expenses: 4100000, salary_burden: 4100000 } },
    { label: "Double marketing budget", deltas: { monthly_expenses: 3800000 } },
    { label: "Revenue falls 15%", deltas: { monthly_revenue: -6300000 } },
    { label: "Customer churn +50%", deltas: { churn_rate: 0.02, csat: -0.4 } },
    { label: "Close ₹5Cr new deals", deltas: { monthly_revenue: 8000000 } },
];

export function MoneyMeterPanel({ data, onSimulate, compact = false }) {
    const [sim, setSim] = useState(null);
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(false);
    const score = data?.score ?? 0;
    const trend = data?.trend || [];

    async function run(preset) {
        if (!onSimulate) return;
        setActive(preset.label); setBusy(true);
        try { setSim(await onSimulate(preset.deltas)); } catch (_e) { /* ignore */ } finally { setBusy(false); }
    }
    function reset() { setSim(null); setActive(null); }

    const future = sim ? sim.future : null;

    return (
        <div className="glass-strong rounded-3xl p-6" data-testid="money-meter-panel">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="shrink-0 self-center">
                    <MoneyMeterGauge score={score} future={future} size={compact ? 160 : 200} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div>
                            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-violet-600">Money Meter</div>
                            <div className="text-[13px] text-neutral-500">Live financial-health score · {typeof data?.confidence === "number" ? `${data.confidence}% confidence` : "—"}</div>
                        </div>
                        {sim && <button onClick={reset} className="text-[12px] text-neutral-500 hover:text-black" data-testid="money-meter-reset">Reset</button>}
                    </div>

                    {sim ? (
                        <div className="mt-3 rounded-2xl border border-violet-100 bg-violet-50/40 p-3" data-testid="money-meter-sim-result">
                            <div className="text-[13px] text-neutral-700"><b>{active}</b>: {sim.current} → <b style={{ color: bandColor(sim.future) }}>{sim.future}</b> ({sim.difference >= 0 ? "+" : ""}{sim.difference})</div>
                            <div className="text-[12px] text-neutral-500 mt-1">{sim.reason}</div>
                            {sim.horizons?.length > 0 && (
                                <div className="mt-2 grid grid-cols-5 gap-1.5">
                                    {sim.horizons.map((h) => (
                                        <div key={h.horizon} className="text-center rounded-lg bg-white/70 py-1.5">
                                            <div className="font-display text-sm" style={{ color: bandColor(h.money_meter) }}>{h.money_meter}</div>
                                            <div className="text-[8.5px] uppercase tracking-wide text-neutral-400">{h.horizon}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {trend.length > 1 && <div className="mt-2"><Sparkline points={trend} color={bandColor(score)} /></div>}
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-emerald-600 mb-1">Positive Drivers</div>
                                    <ul className="space-y-0.5">{(data?.positive_drivers || []).slice(0, 4).map((d, i) => <li key={i} className="text-[12px] text-neutral-600 flex gap-1.5"><span className="text-emerald-500">▲</span>{d}</li>)}
                                        {!(data?.positive_drivers || []).length && <li className="text-[12px] text-neutral-400">—</li>}</ul>
                                </div>
                                <div>
                                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-rose-500 mb-1">Negative Drivers</div>
                                    <ul className="space-y-0.5">{(data?.negative_drivers || []).slice(0, 4).map((d, i) => <li key={i} className="text-[12px] text-neutral-600 flex gap-1.5"><span className="text-rose-500">▼</span>{d}</li>)}
                                        {!(data?.negative_drivers || []).length && <li className="text-[12px] text-neutral-400">—</li>}</ul>
                                </div>
                            </div>
                        </>
                    )}

                    {onSimulate && (
                        <div className="mt-4">
                            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-1.5">What-if simulation</div>
                            <div className="flex flex-wrap gap-1.5">
                                {PRESETS.map((p) => (
                                    <button key={p.label} onClick={() => run(p)} disabled={busy} data-testid={`money-meter-whatif-${p.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                        className={`text-[12px] rounded-full px-3 py-1.5 border transition-colors ${active === p.label ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-100 hover:bg-violet-50"}`}>
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Small inline before→after pill for chat/cards.
export function MoneyMeterDelta({ before, after, reason }) {
    if (before == null && after == null) return null;
    const b = Math.round(before ?? after), a = Math.round(after ?? before);
    const diff = Math.round((a - b) * 10) / 10;
    return (
        <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-full bg-white border border-black/[0.07] px-3 py-1.5" data-testid="money-meter-delta" title={reason || ""}>
            <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-violet-600">Money Meter</span>
            <span className="text-[13px] font-medium" style={{ color: bandColor(b) }}>{b}</span>
            <span className="text-neutral-400">→</span>
            <span className="text-[13px] font-semibold" style={{ color: bandColor(a) }}>{a}</span>
            <span className="text-[12px] font-medium" style={{ color: diff >= 0 ? "#16a34a" : "#e11d48" }}>({diff >= 0 ? "+" : ""}{diff})</span>
        </div>
    );
}
